import React, {
   CSSProperties,
   PropsWithChildren,
   ReactNode,
   Ref,
   RefObject,
   useMemo,
   useRef
} from 'react';
import { TableOfContents } from './TableOfContents';
import { BorderPanel } from '../../components/layout/borderpanel';
import { Panel } from '../../components/layout/panel';

/**
 * {@docCategory ContentPanel}
 */
export interface ClickInfo {
   id: string;
   idPath: Array<string>;
   item: TableOfContentsItemProps;
   domEvent: Event;
}

/**
 * {@docCategory ContentPanel}
 */
export interface TableOfContentsItemProps {
   id: string;
   title: string;
   onClick?: (param: ClickInfo) => void;
   items?: TableOfContentsItemProps[];
}

/**
 * {@docCategory ContentPanel}
 */
export interface TableOfContentsProps {
   items: TableOfContentsItemProps[];
   selectedId?: string;
   onClick?: (param: ClickInfo) => void;
}

/**
 * {@docCategory ContentPanel}
 */
export interface ContentPanelProps {
   /**
    * An optional header.
    */
   header?: ContentPanelHeaderProps | ReactNode;

   /**
    * An optional footer.
    */
   footer?: ContentPanelFooterProps | ReactNode;

   /**
    * Display an optional pinned table of contents to help navigate within the page
    */
   tableOfContents?: TableOfContentsProps;

   /**
    * Used to enable the built in overflow configuration for the content area.
    * @defaultvalue false
    */
   scroll?: boolean;

   /**
    * Disable the padding in the content area.
    *
    * @defaultvalue false
    */
   disableContentPadding?: boolean;

   /**
    * Sets a className on the panel.
    */
   className?: string;

   /**
    * Sets styles on the panel.
    */
   style?: CSSProperties;

   /**
    * Sets the reference property
    */
   ref?: Ref<HTMLDivElement>;
}

export interface ContentPanelHeaderProps {
   content?: ReactNode;
}

export interface ContentPanelFooterProps {
   content?: ReactNode;
}

const header = (disableContentPadding: boolean, content?: ReactNode) => {
   if (content) {
      const className = disableContentPadding ? 'uf-content-panel-header-no-content-padding' : 'uf-content-panel-header';

      return (
         <div className={className}>
            {content}
         </div>
      );
   }

   return undefined;
};

const footer = (disableContentPadding: boolean, content?: ReactNode) => {
   if (content) {
      const className = disableContentPadding ? 'uf-content-panel-footer-no-content-padding' : 'uf-content-panel-footer';

      return (
         <div className={className}>
            {content}
         </div>
      );
   }

   return undefined;
};

const getScrollContainerFunction = (scrollContainerRef?: RefObject<HTMLDivElement>) => {
   return () => {
      if (scrollContainerRef && scrollContainerRef.current) {
         return scrollContainerRef.current;
      }

      return window;
   };
};

const tableOfContents = (props?: TableOfContentsProps, scrollContainerRef?: RefObject<HTMLDivElement>) => {
   if (props) {
      return <TableOfContents
            {...props}
            scrollContainer={getScrollContainerFunction(scrollContainerRef)}
         />;

   }
   return undefined;
};

export const ContentPanel: React.FC<PropsWithChildren<ContentPanelProps>> = React.forwardRef<HTMLDivElement>(
   (props: PropsWithChildren<ContentPanelProps>, ref: Ref<HTMLDivElement>) => {
      const scrollContainerRef = useRef<HTMLDivElement>(null);
      const contentPadding = props.disableContentPadding === true ? false : 'medium';

      const hasNavigationPanel = props.tableOfContents !== undefined;

      let className = 'uf-content-panel';

      if (hasNavigationPanel) {
         className += ' uf-content-panel-has-toc';
      }

      if (props.className) {
         className += ` ${props.className}`;
      }

      const headerContent = useMemo(() => (props.header ? (React.isValidElement(props.header) ?
                                                         props.header :
                                                         (props.header as ContentPanelHeaderProps).content)
                                                      : undefined),
                                    [props.header]);
      const footerContent = useMemo(() => (props.footer ? (React.isValidElement(props.footer) ?
                                                           props.footer :
                                                           (props.footer as ContentPanelFooterProps).content)
                                                        : undefined),
                                    [props.footer]);

      return (
         <BorderPanel
            className={className}
            style={props.style}
            ref={ref}
            top={header(props.disableContentPadding === true, headerContent)}
            bottom={footer(props.disableContentPadding === true, footerContent)}
            right={
               tableOfContents(props.tableOfContents, scrollContainerRef)
            }
         >
            <Panel className={'uf-content-panel-inner-panel'} fill={true} scroll={props.scroll} padding={contentPadding} ref={scrollContainerRef}>
               {props.children}
            </Panel>
         </BorderPanel>
      );
   });
